// Worksheet Wizard Store - Zustand state management
import { create } from 'zustand';
import type { Partner } from '../rental/types';
import { calculateItemTotal, calculateLaborPrice } from './mock-data';
import type { LaborNorm, Part, Product, RepairType, WorksheetItem } from './types';
import { WorksheetPriority, WorksheetType } from './types';

export type { RepairType } from './types';

/** E-105 Phase 2 S5 MVP — accordion panel azonosítók. */
export type WorksheetPanelId = 'partner' | 'repairType' | 'machineCategory' | 'product' | 'problem';

export interface WorksheetPanelValidity {
  isValid: boolean;
  summary?: string;
}

interface WorksheetWizardState {
  // Navigation (legacy linear stepper — megmarad a S5 MVP accordion mellett
  // backward-compat-ért a meglévő step-based tesztek + store sanity miatt)
  step: number;

  // E-105 Phase 2 S5 MVP — accordion open/closed state
  openPanels: Set<WorksheetPanelId>;

  // Step 1: Partner
  partner: Partner | null;

  // Step 2: Repair Type
  repairType: RepairType | null;

  // Step 2b: Machine Category
  machineCategoryId: string | null;

  // Step 3: Product
  product: Product | null;
  deviceName: string;
  serialNumber: string;

  // Step 3: Problem
  worksheetType: WorksheetType;
  priority: WorksheetPriority;
  faultDescription: string;
  costLimit: number | null;

  // Step 4: Diagnostics & Items
  diagnosis: string;
  workPerformed: string;
  items: WorksheetItem[];

  // Step 5: Summary
  estimatedCompletionDate: Date | null;
  internalNote: string;

  // Story 70-1E-a: expired ID card override (FR70 audit on POST /worksheets)
  overrideExpiredId: boolean;
  overrideReason: string;

  // Computed
  totals: {
    netTotal: number;
    vatTotal: number;
    grossTotal: number;
    partsCount: number;
    laborCount: number;
  };

  // Actions
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;

  setPartner: (partner: Partner | null) => void;
  setRepairType: (type: RepairType | null) => void;
  setMachineCategory: (id: string) => void;
  setProduct: (product: Product | null) => void;
  setDeviceInfo: (name: string, serial: string) => void;

  setProblemInfo: (
    type: WorksheetType,
    priority: WorksheetPriority,
    description: string,
    costLimit: number | null
  ) => void;

  setDiagnostics: (diagnosis: string, workPerformed: string) => void;
  addPart: (part: Part, quantity: number) => void;
  addLabor: (norm: LaborNorm, quantity: number) => void;
  addCustomItem: (description: string, unitPrice: number, quantity: number) => void;
  removeItem: (itemId: string) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;

  setSummaryInfo: (completionDate: Date | null, note: string) => void;

  /** Story 70-1E-a: override expired ID card (maps to DTO overrideExpiredId). */
  setExpiredIdOverride: (override: boolean, reason: string) => void;

  canProceed: () => boolean;

  // E-105 Phase 2 S5 MVP — accordion actions
  togglePanel: (id: WorksheetPanelId) => void;
  openPanel: (id: WorksheetPanelId) => void;
  closePanel: (id: WorksheetPanelId) => void;
  getPanelValidity: (id: WorksheetPanelId) => WorksheetPanelValidity;

  reset: () => void;
}

const INITIAL_OPEN_PANELS = new Set<WorksheetPanelId>(['partner']);

const initialState = {
  step: 0,
  openPanels: INITIAL_OPEN_PANELS,
  partner: null,
  repairType: null as RepairType | null,
  machineCategoryId: null as string | null,
  product: null,
  deviceName: '',
  serialNumber: '',
  worksheetType: WorksheetType.FIZETOS,
  priority: WorksheetPriority.NORMAL,
  faultDescription: '',
  costLimit: null,
  diagnosis: '',
  workPerformed: '',
  items: [] as WorksheetItem[],
  estimatedCompletionDate: null,
  internalNote: '',
  overrideExpiredId: false,
  overrideReason: '',
  totals: {
    netTotal: 0,
    vatTotal: 0,
    grossTotal: 0,
    partsCount: 0,
    laborCount: 0,
  },
};

function repairTypeLabel(type: RepairType): string {
  switch (type) {
    case 'FIZETOS':
      return 'Fizetős';
    case 'GARANCIALIS':
      return 'Garanciális';
    case 'SZERZODESES':
      return 'Szerződéses';
  }
}

function calculateTotals(items: WorksheetItem[]) {
  const netTotal = items.reduce((sum, item) => sum + item.netAmount, 0);
  const grossTotal = items.reduce((sum, item) => sum + item.grossAmount, 0);
  const vatTotal = grossTotal - netTotal;
  const partsCount = items.filter(i => i.type === 'PART').length;
  const laborCount = items.filter(i => i.type === 'LABOR').length;

  return { netTotal, vatTotal, grossTotal, partsCount, laborCount };
}

export const useWorksheetWizardStore = create<WorksheetWizardState>((set, get) => ({
  ...initialState,

  // Navigation
  // 5-step wizard: 0=Partner, 1=Javítás típusa, 2=Gépkategória, 3=Gép, 4=Hiba (Diagnosztika a detail page-ről)
  nextStep: () => set(state => ({ step: Math.min(state.step + 1, 4) })),
  prevStep: () => set(state => ({ step: Math.max(state.step - 1, 0) })),
  goToStep: step => set({ step: Math.max(0, Math.min(step, 4)) }),

  // Partner
  // Story 70-1E-a: partner swap resets expired ID override — new partner's
  // expiry may differ, override cannot silently carry over.
  setPartner: partner => set({ partner, overrideExpiredId: false, overrideReason: '' }),

  // Story 70-1E-a: expired ID override setter
  setExpiredIdOverride: (override, reason) =>
    set({ overrideExpiredId: override, overrideReason: reason }),

  // Repair Type
  setRepairType: repairType => set({ repairType }),

  // Machine Category
  setMachineCategory: (machineCategoryId: string) => set({ machineCategoryId }),

  // Product
  setProduct: product =>
    set({
      product,
      deviceName: product?.name ?? '',
      serialNumber: product?.serialNumber ?? '',
    }),
  setDeviceInfo: (deviceName, serialNumber) => set({ deviceName, serialNumber }),

  // Problem
  setProblemInfo: (worksheetType, priority, faultDescription, costLimit) =>
    set({
      worksheetType,
      priority,
      faultDescription,
      costLimit,
    }),

  // Diagnostics
  setDiagnostics: (diagnosis, workPerformed) => set({ diagnosis, workPerformed }),

  addPart: (part, quantity) =>
    set(state => {
      const { netAmount, grossAmount } = calculateItemTotal(part.unitPrice, quantity);
      const newItem: WorksheetItem = {
        id: `part-${Date.now()}`,
        type: 'PART',
        description: part.name,
        quantity,
        unitPrice: part.unitPrice,
        vatRate: 27,
        netAmount,
        grossAmount,
        partId: part.id,
      };
      const items = [...state.items, newItem];
      return { items, totals: calculateTotals(items) };
    }),

  addLabor: (norm, quantity) =>
    set(state => {
      // unitPrice = per-egység munkadíj (NEM a teljes ár) — különben az
      // updateItemQuantity re-edit duplán szorozna (perUnit×origQty×newQty).
      const unitPrice = calculateLaborPrice(norm, 1);
      const { netAmount, grossAmount } = calculateItemTotal(unitPrice, quantity);
      const newItem: WorksheetItem = {
        id: `labor-${Date.now()}`,
        type: 'LABOR',
        description: `${norm.code} - ${norm.description} (${norm.minutes} perc)`,
        quantity,
        unitPrice,
        vatRate: 27,
        netAmount,
        grossAmount,
        normId: norm.id,
      };
      const items = [...state.items, newItem];
      return { items, totals: calculateTotals(items) };
    }),

  addCustomItem: (description, unitPrice, quantity) =>
    set(state => {
      const { netAmount, grossAmount } = calculateItemTotal(unitPrice, quantity);
      const newItem: WorksheetItem = {
        id: `custom-${Date.now()}`,
        type: 'OTHER',
        description,
        quantity,
        unitPrice,
        vatRate: 27,
        netAmount,
        grossAmount,
      };
      const items = [...state.items, newItem];
      return { items, totals: calculateTotals(items) };
    }),

  removeItem: itemId =>
    set(state => {
      const items = state.items.filter(i => i.id !== itemId);
      return { items, totals: calculateTotals(items) };
    }),

  updateItemQuantity: (itemId, quantity) =>
    set(state => {
      const items = state.items.map(item => {
        if (item.id !== itemId) return item;
        const { netAmount, grossAmount } = calculateItemTotal(item.unitPrice, quantity);
        return { ...item, quantity, netAmount, grossAmount };
      });
      return { items, totals: calculateTotals(items) };
    }),

  // Summary
  setSummaryInfo: (estimatedCompletionDate, internalNote) =>
    set({
      estimatedCompletionDate,
      internalNote,
    }),

  // Validation
  canProceed: () => {
    const state = get();
    switch (state.step) {
      case 0: // Partner
        return state.partner !== null;
      case 1: // Repair Type
        return state.repairType !== null;
      case 2: // Machine Category
        return state.machineCategoryId !== null;
      case 3: // Product
        return state.deviceName.trim().length > 0;
      case 4: // Problem
        return state.faultDescription.trim().length > 0;
      case 5: // Diagnostics
        return state.diagnosis.trim().length > 0;
      case 6: // Summary
        return true;
      default:
        return false;
    }
  },

  // E-105 Phase 2 S5 MVP — accordion actions
  togglePanel: (id: WorksheetPanelId) =>
    set(state => {
      const next = new Set(state.openPanels);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { openPanels: next };
    }),

  openPanel: (id: WorksheetPanelId) =>
    set(state => {
      if (state.openPanels.has(id)) return {};
      const next = new Set(state.openPanels);
      next.add(id);
      return { openPanels: next };
    }),

  closePanel: (id: WorksheetPanelId) =>
    set(state => {
      if (!state.openPanels.has(id)) return {};
      const next = new Set(state.openPanels);
      next.delete(id);
      return { openPanels: next };
    }),

  /**
   * E-105 Phase 2 S5 MVP — per-panel validity + rövid summary
   * (collapsed állapotban megjelenő szöveg az accordion headeren).
   *
   * Minden panel független — a szomszéd panel nyitva/zárva állapotát NEM
   * befolyásolja. Ez különbözik a rental wizardtól, ahol az invalid panel
   * "expandInvalid" gombbal újrajeleníthető. Worksheet MVP-ben ezt az
   * egyszerűsítést választjuk (drift dual-gate Phase 3 story-ban jön).
   */
  getPanelValidity: (id: WorksheetPanelId): WorksheetPanelValidity => {
    const state = get();
    switch (id) {
      case 'partner':
        return {
          isValid: state.partner !== null,
          ...(state.partner ? { summary: state.partner.name } : {}),
        };
      case 'repairType':
        return {
          isValid: state.repairType !== null,
          ...(state.repairType ? { summary: repairTypeLabel(state.repairType) } : {}),
        };
      case 'machineCategory':
        return {
          isValid: state.machineCategoryId !== null,
        };
      case 'product': {
        const identified = state.product !== null || state.deviceName.trim().length > 0;
        const label = state.product?.name ?? (state.deviceName.trim() || undefined);
        return {
          isValid: identified,
          ...(label ? { summary: label } : {}),
        };
      }
      case 'problem': {
        const hasDescription = state.faultDescription.trim().length > 0;
        const summary = hasDescription
          ? state.faultDescription.slice(0, 60) + (state.faultDescription.length > 60 ? '…' : '')
          : undefined;
        return {
          isValid: hasDescription,
          ...(summary ? { summary } : {}),
        };
      }
    }
  },

  reset: () => set({ ...initialState, openPanels: new Set(INITIAL_OPEN_PANELS) }),
}));
